import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Loader2, TrendingUp, ArrowRight, RefreshCw, ChevronDown, Trash2, Clock, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/public/PublicLayout';
import { authSelector } from '../../features/auth/slice';
import SEO from '../../components/SEO';
import { PUBLIC_BASE } from './config';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

const CATEGORY_COLORS = {
  '마케팅 전략': 'bg-blue-500/15 text-blue-400',
  '유통 가이드': 'bg-orange-500/15 text-orange-400',
  '제작 노하우': 'bg-emerald-500/15 text-emerald-400',
  '트렌드 분석': 'bg-amber-500/15 text-amber-400',
  '수익화 전략': 'bg-purple-500/15 text-purple-400',
  '아티스트 성장': 'bg-cyan-500/15 text-cyan-400',
};

const PROGRESS_STEPS = [
  { label: 'AI 콘텐츠 생성', duration: 180 },
  { label: '커버 이미지 생성', duration: 60 },
  { label: '본문 이미지 생성', duration: 120 },
  { label: '저장 중', duration: 20 },
];
const TOTAL_ESTIMATED = PROGRESS_STEPS.reduce((sum, s) => sum + s.duration, 0);

function getMondayDate() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split('T')[0];
}

function getWeekLabel() {
  const now = new Date();
  const weekOfMonth = Math.ceil(now.getDate() / 7);
  return `${now.getMonth() + 1}월 ${weekOfMonth}주차`;
}

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const created = new Date(dateStr);
  const now = new Date();
  const diffMs = now - created;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return '오늘 업데이트';
  if (diffDays === 1) return '어제 업데이트';
  if (diffDays < 7) return `${diffDays}일 전 업데이트`;
  const m = created.getMonth() + 1;
  const d = created.getDate();
  return `${m}월 ${d}일 업데이트`;
}

function ProgressBar({ elapsed }) {
  const progress = Math.min((elapsed / TOTAL_ESTIMATED) * 100, 95);

  let currentStep = 0;
  let accumulated = 0;
  for (let i = 0; i < PROGRESS_STEPS.length; i++) {
    accumulated += PROGRESS_STEPS[i].duration;
    if (elapsed < accumulated) { currentStep = i; break; }
    if (i === PROGRESS_STEPS.length - 1) currentStep = i;
  }

  return (
    <div className="w-full max-w-sm mt-6">
      <div className="flex justify-between text-[11px] text-slate-500 mb-2">
        <span>{PROGRESS_STEPS[currentStep].label}...</span>
        <span>{Math.floor(elapsed / 60)}:{String(Math.floor(elapsed % 60)).padStart(2, '0')} 경과</span>
      </div>
      <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-blue-500 rounded-full transition-all duration-1000 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="flex justify-between mt-2 gap-1">
        {PROGRESS_STEPS.map((step, i) => (
          <div key={step.label} className="flex-1 text-center">
            <div className={`h-0.5 rounded-full mb-1 ${i <= currentStep ? 'bg-sky-500/40' : 'bg-white/5'}`} />
            <span className={`text-[9px] ${i <= currentStep ? 'text-sky-400/70' : 'text-slate-600'}`}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 웹진 스타일 카드 ── */
function TrendCard({ trend, isNew, isAdmin, onDelete }) {
  const navigate = useNavigate();
  const colorClass = CATEGORY_COLORS[trend.category] || 'bg-sky-500/15 text-sky-400';

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`"${trend.title}" 아티클을 삭제하시겠습니까?`)) {
      onDelete(trend.id);
    }
  };

  return (
    <div
      onClick={() => navigate(`${PUBLIC_BASE}/magazine/music-trends/${trend.id}`)}
      className="group flex flex-col sm:flex-row bg-[#182334] rounded-2xl overflow-hidden border border-white/5 cursor-pointer hover:border-white/15 transition-all hover:shadow-xl hover:shadow-black/20">
      {/* 이미지 영역 */}
      <div className="relative sm:w-[45%] shrink-0">
        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
          <img
            src={trend.coverImage || FALLBACK_IMG}
            alt={trend.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors hidden sm:block" />
        </div>
        {isNew && (
          <span className="absolute top-3 left-3 bg-sky-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide shadow-lg">
            NEW
          </span>
        )}
        {isAdmin && (
          <button
            onClick={handleDelete}
            className="absolute top-3 right-3 p-1.5 bg-black/50 hover:bg-red-500/80 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
            title="삭제">
            <Trash2 size={14} className="text-white" />
          </button>
        )}
      </div>

      {/* 텍스트 영역 */}
      <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between min-w-0">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className={`inline-block text-[11px] font-medium px-2.5 py-1 rounded-full ${colorClass}`}>
              {trend.category}
            </span>
          </div>
          <h3 className="text-white font-bold text-base sm:text-lg mb-2 leading-snug line-clamp-2 group-hover:text-sky-300 transition-colors">
            {trend.title}
          </h3>
          <p className="text-slate-400 text-xs sm:text-sm leading-relaxed line-clamp-3 mb-3">
            {trend.summary}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1 text-slate-600 text-[11px]">
            <Clock size={11} />
            {getTimeAgo(trend.createdAt)}
          </span>
          <span className="flex items-center gap-1 text-slate-500 group-hover:text-sky-400 text-xs transition-colors">
            자세히 보기
            <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </div>
  );
}

function PublicMusicTrendPage() {
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const { me } = useSelector(authSelector.selectAuthState);
  const isAdmin = me?.adminYn === 'Y';
  const currentMonday = getMondayDate();

  const fetchTrends = useCallback((offset = 0, limit = 4) => {
    return axios.get('/public/music-trends', { params: { offset, limit } }).then((res) => res.data);
  }, []);

  const startTimer = useCallback(() => {
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopTimer();
  }, [stopTimer]);

  useEffect(() => {
    fetchTrends()
      .then((data) => {
        if (data.items?.length === 0) {
          setGenerating(true);
          startTimer();
          const timer = setTimeout(() => {
            setGenerating(false);
            stopTimer();
            fetchTrends().then((d) => {
              setTrends(d.items || []);
              setHasMore(d.hasMore || false);
            }).catch(() => {});
          }, 30000);
          return () => clearTimeout(timer);
        }
        setTrends(data.items || []);
        setHasMore(data.hasMore || false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fetchTrends, startTimer, stopTimer]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchTrends(trends.length, 4)
      .then((data) => {
        setTrends((prev) => [...prev, ...(data.items || [])]);
        setHasMore(data.hasMore || false);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/public/music-trends/${id}`);
      setTrends((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      alert('삭제에 실패했습니다.');
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('뮤직트렌드 3개를 새로 추가 생성하시겠습니까?\n(AI 이미지 생성 포함, 약 5~8분 소요)')) return;
    setGenerating(true);
    startTimer();
    try {
      const current = await fetchTrends(0, 1);
      const initialTotal = current.total || 0;

      await axios.post('/public/music-trends/regenerate');

      let pollElapsed = 0;
      const poll = setInterval(() => {
        pollElapsed += 15000;
        fetchTrends()
          .then((data) => {
            if ((data.total || 0) > initialTotal) {
              setTrends(data.items || []);
              setHasMore(data.hasMore || false);
              setGenerating(false);
              stopTimer();
              clearInterval(poll);
            } else if (pollElapsed >= 600000) {
              setGenerating(false);
              stopTimer();
              clearInterval(poll);
            }
          })
          .catch(() => {});
      }, 15000);
    } catch (err) {
      console.error('[재생성] 실패:', err);
      setGenerating(false);
      stopTimer();
    }
  };

  return (
    <PublicLayout>
      <SEO
        title="뮤직트렌드"
        description="AI가 분석한 음악 트렌드 아티클을 읽어보세요."
        path="/magazine/music-trends"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-10">
        {/* 헤더 */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight text-center">매거진</h1>
          <p className="text-center text-slate-500 text-sm mt-1">음악 트렌드와 문화 이야기</p>
          {/* 탭 */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <Link
              to={`${PUBLIC_BASE}/magazine/music-trends`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors bg-sky-500/20 text-sky-400 border border-sky-500/30">
              <TrendingUp size={14} />
              뮤직트렌드
            </Link>
            <Link
              to={`${PUBLIC_BASE}/magazine/hot-place`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors bg-white/[0.04] text-slate-400 border border-white/[0.08] hover:bg-white/[0.08]">
              <BookOpen size={14} />
              핫플힙플
            </Link>
          </div>
        </div>

        <div className="flex items-end justify-between mb-6">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-medium">
              {getWeekLabel()}
            </span>
          </div>
          {isAdmin && !generating && (
            <button
              onClick={handleRegenerate}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-sky-400 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors">
              <RefreshCw size={12} />
              재생성
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3 text-slate-400">
            <Loader2 size={20} className="animate-spin" />
            <span>로딩 중...</span>
          </div>
        ) : generating ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-slate-400">
            <div className="w-12 h-12 rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
            <p className="text-sm">콘텐츠를 생성하고 있습니다...</p>
            <p className="text-xs text-slate-600">AI 콘텐츠 + 이미지 생성, 약 5~8분 소요됩니다</p>
            <ProgressBar elapsed={elapsed} />
          </div>
        ) : trends.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
            <TrendingUp size={40} />
            <p>콘텐츠가 없습니다.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {trends.map((trend) => (
                <TrendCard
                  key={trend.id}
                  trend={trend}
                  isNew={trend.weekOf === currentMonday}
                  isAdmin={isAdmin}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-slate-300 text-sm transition-colors disabled:opacity-50">
                  {loadingMore ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                  지난 콘텐츠 더보기
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </PublicLayout>
  );
}

export default PublicMusicTrendPage;
